import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTheme } from 'react-native-paper';
import type { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { ArrowLeft, Menu as MenuIcon } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function AppHeader({
  navigation,
  options,
  back,
}: NativeStackHeaderProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const titleText = typeof options.title === 'string' ? options.title : '';
  const headerTintColor =
    (options.headerTintColor as string | undefined) ?? theme.colors.onSurface;
  const headerBg =
    (options.headerStyle as any)?.backgroundColor ?? theme.colors.surface;
  const isTransparent = Boolean(options.headerTransparent);
  const showShadow = options.headerShadowVisible !== false;
  const titleAlign =
    (options.headerTitleAlign as 'left' | 'center' | undefined) ?? 'center';
  const renderTitle = () => {
    if (typeof options.headerTitle === 'function') {
      return options.headerTitle({
        children: titleText,
        tintColor: headerTintColor,
      });
    }
    if (typeof options.headerTitle === 'string') {
      return (
        <Text
          numberOfLines={1}
          style={[styles.title, { color: theme.colors.onSurface }]}
        >
          {options.headerTitle}
        </Text>
      );
    }
    return (
      <Text
        numberOfLines={1}
        style={[
          styles.title,
          { color: headerTintColor },
          options.headerTitleStyle as any,
        ]}
      >
        {titleText}
      </Text>
    );
  };

  const canGoBack = Boolean(back);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: isTransparent ? 'transparent' : headerBg,
          borderBottomColor:
            theme.colors.outlineVariant ?? theme.colors.outline,
          paddingTop: insets.top,
        },
      ]}
    >
      <View
        style={[
          styles.inner,
          {
            borderBottomWidth: showShadow ? StyleSheet.hairlineWidth : 0,
            borderBottomColor:
              theme.colors.outlineVariant ?? theme.colors.outline,
          },
        ]}
      >
        <View style={styles.left}>
          {options.headerLeft ? (
            options.headerLeft({ tintColor: headerTintColor, canGoBack })
          ) : canGoBack ? (
            <Pressable
              onPress={navigation.goBack}
              accessibilityRole='button'
              accessibilityLabel='Go back'
              hitSlop={12}
              style={styles.backButton}
              testID='app-header-back'
            >
              <ArrowLeft color={headerTintColor} size={22} />
            </Pressable>
          ) : (
            <Pressable
              onPress={() => {
                const parent: any = (navigation as any).getParent?.();
                const maybeDrawer: any = parent?.getParent?.() ?? parent;
                if (maybeDrawer?.openDrawer) {
                  maybeDrawer.openDrawer();
                }
              }}
              accessibilityRole='button'
              accessibilityLabel='Open menu'
              hitSlop={12}
              style={styles.backButton}
              testID='app-header-menu'
            >
              <MenuIcon color={headerTintColor} size={22} />
            </Pressable>
          )}
        </View>
        <View
          style={[
            styles.center,
            titleAlign === 'left' && {
              alignItems: 'flex-start',
              paddingLeft: 8,
            },
          ]}
        >
          {renderTitle()}
        </View>
        <View style={styles.right}>
          {options.headerRight?.({ tintColor: headerTintColor, canGoBack }) ??
            null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  inner: {
    minHeight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  left: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  backButton: {
    padding: 8,
    borderRadius: 8,
  },
  placeholder: {
    width: 32,
    height: 32,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
  },
  right: {
    width: 48,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
});

export default AppHeader;
